// ═══════════════════════════════════════════════════════════
// Settings — jurisdiction config, departments, templates,
// exemption codes, and system administration
// ═══════════════════════════════════════════════════════════

import { useState, useEffect, useRef } from 'react';
import {
  IconBuilding, IconFileText, IconScale, IconLoader2,
  IconPlus, IconCheck, IconChevronDown, IconChevronUp,
  IconGitBranch, IconTrash, IconArrowUp, IconArrowDown,
} from '@tabler/icons-react';
import { listDepartments, createDepartment, updateDepartment, deleteDepartment, type Department } from '../../../api/departments';
import { backfillPriority } from '../../../api/requests';
import { listHolidays, createHoliday, deleteHoliday, type Holiday } from '../../../api/holidays';
import { listTemplates, seedTemplates, type CorrTemplate } from '../../../api/correspondence';
import {
  listJurisdictions, updateJurisdiction, listExemptionCodes,
  createExemptionCode, updateExemptionCode, seedExemptionCodes,
  type Jurisdiction, type ExemptionCode,
} from '../../../api/jurisdiction';
import {
  listWorkflows, createWorkflow, updateWorkflow, deleteWorkflow,
  listWorkflowSteps, createWorkflowStep, updateWorkflowStep, deleteWorkflowStep,
  type Workflow, type WorkflowStep, type CreateWorkflowStepInput,
} from '../../../api/workflows';
import { type TaskType } from '../../../api/tasks';
import {
  listLibraryDocuments, createLibraryDocument, updateLibraryDocument,
  deleteLibraryDocument, type LibraryDocument,
} from '../../../api/library';
import { useAuthStore } from '../../../store/authStore';

const SETTINGS_TABS = [
  { id: 'jurisdiction', label: 'Jurisdiction',    icon: IconScale },
  { id: 'departments',  label: 'Departments',     icon: IconBuilding },
  { id: 'templates',    label: 'Templates',       icon: IconFileText },
  { id: 'exemptions',   label: 'Exemption Codes', icon: IconScale },
  { id: 'workflows',    label: 'Workflows',       icon: IconGitBranch },
  { id: 'holidays',     label: 'Holidays',        icon: IconCheck },
  { id: 'library',      label: 'Published Records', icon: IconFileText },
  { id: 'maintenance',  label: 'Maintenance',     icon: IconLoader2 },
];

const JURISDICTION_ID = import.meta.env.VITE_JURISDICTION_ID ?? '1';

export const SIGNATURE_BLOCK_KEY = 'foia:signatureBlock';

// ── Signature Block Card ──────────────────────────────────

function SignatureBlockCard() {
  const [sig, setSig]     = useState(() => localStorage.getItem(SIGNATURE_BLOCK_KEY) ?? '');
  const [saved, setSaved] = useState(false);

  function handleSave() {
    localStorage.setItem(SIGNATURE_BLOCK_KEY, sig);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  }

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--color-neutral-100)', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-neutral-600)' }}>
        Correspondence Signature Block
      </div>
      <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ fontSize: 12, color: 'var(--color-neutral-500)' }}>
          Automatically appended to AI-drafted and template correspondence. Use plain text; line breaks are preserved.
        </div>
        <textarea
          className="form-input"
          rows={5}
          style={{ fontFamily: 'inherit', resize: 'vertical', fontSize: 14, lineHeight: 1.6 }}
          placeholder={'Sincerely,\n\nJane Smith\nRecords Officer\nrecords@example.gov · (555) 012-3456'}
          value={sig}
          onChange={e => setSig(e.target.value)}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button className="btn btn-secondary btn-sm" style={{ gap: 5 }} onClick={handleSave}>
            {saved ? <><IconCheck size={12} /> Saved</> : 'Save Signature'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Jurisdiction Tab ──────────────────────────────────────

function JurisdictionTab() {
  const [juris, setJuris]     = useState<Jurisdiction | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [form, setForm]       = useState<Partial<Jurisdiction>>({});
  const [saved, setSaved]     = useState(false);

  useEffect(() => {
    listJurisdictions().then(list => {
      const j = list.find(x => String(x.recordId) === JURISDICTION_ID) ?? list[0] ?? null;
      setJuris(j);
      if (j) setForm({
        name:               j.name,
        statutoryDays:      j.statutoryDays,
        extensionAllowed:   j.extensionAllowed,
        maxExtensionDays:   j.maxExtensionDays,
        businessDaysOnly:   j.businessDaysOnly,
        feesAllowed:        j.feesAllowed,
        alertThresholdDays: j.alertThresholdDays,
        contactInfo:        j.contactInfo ?? '',
        portalUrl:          j.portalUrl ?? '',
        primaryColor:       j.primaryColor ?? 'var(--color-primary-700)',
        acknowledgmentRequired: j.acknowledgmentRequired,
        acknowledgmentDays: j.acknowledgmentDays,
      });
    }).finally(() => setLoading(false));
  }, []);

  async function handleSave() {
    if (!juris) return;
    setSaving(true);
    try {
      const updated = await updateJurisdiction(juris.recordId, form);
      setJuris(updated);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}><IconLoader2 size={20} style={{ animation: 'spin 1s linear infinite', color: 'var(--color-neutral-400)' }} /></div>;
  if (!juris) return <div style={{ padding: 32, color: 'var(--color-neutral-400)', fontSize: 14 }}>No jurisdiction configured. Run the setup script to create one.</div>;

  return (
    <div style={{ maxWidth: 860, margin: '0 auto' }}>
      <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--color-neutral-900)', margin: '0 0 20px' }}>Jurisdiction Settings</h2>

      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--color-neutral-100)', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-neutral-600)' }}>
          Identity
        </div>
        <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="form-field" style={{ marginBottom: 0 }}>
            <label className="form-label">Jurisdiction Name</label>
            <input className="form-input" value={form.name ?? ''} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
          </div>
          <div className="form-field" style={{ marginBottom: 0 }}>
            <label className="form-label">Portal URL</label>
            <input className="form-input" placeholder="https://foia.yourjurisdiction.gov" value={form.portalUrl ?? ''} onChange={e => setForm(f => ({ ...f, portalUrl: e.target.value }))} />
          </div>
          <div className="form-field" style={{ marginBottom: 0 }}>
            <label className="form-label">Contact Info (shown on portal)</label>
            <textarea className="form-input" rows={3} style={{ fontFamily: 'inherit', resize: 'vertical' }} value={form.contactInfo ?? ''} onChange={e => setForm(f => ({ ...f, contactInfo: e.target.value }))} />
          </div>
        </div>
      </div>

      <SignatureBlockCard />

      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--color-neutral-100)', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-neutral-600)' }}>
          Statutory Clock
        </div>
        <div style={{ padding: 16, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div className="form-field" style={{ marginBottom: 0 }}>
            <label className="form-label">Response Days</label>
            <input className="form-input" type="number" min="1" value={form.statutoryDays ?? 20} onChange={e => setForm(f => ({ ...f, statutoryDays: parseInt(e.target.value) || 20 }))} />
          </div>
          <div className="form-field" style={{ marginBottom: 0 }}>
            <label className="form-label">Alert Threshold (days before deadline)</label>
            <input className="form-input" type="number" min="1" value={form.alertThresholdDays ?? 5} onChange={e => setForm(f => ({ ...f, alertThresholdDays: parseInt(e.target.value) || 5 }))} />
          </div>
          <div className="form-field" style={{ marginBottom: 0 }}>
            <label className="form-label">Max Extension Days</label>
            <input className="form-input" type="number" min="0" value={form.maxExtensionDays ?? 0} onChange={e => setForm(f => ({ ...f, maxExtensionDays: parseInt(e.target.value) || 0 }))} />
          </div>
          <div className="form-field" style={{ marginBottom: 0 }}>
            <label className="form-label">Acknowledgment Days</label>
            <input className="form-input" type="number" min="1" value={form.acknowledgmentDays ?? 5} onChange={e => setForm(f => ({ ...f, acknowledgmentDays: parseInt(e.target.value) || 5 }))} />
          </div>
          <div style={{ gridColumn: '1 / -1', display: 'flex', flexWrap: 'wrap', gap: '8px 24px' }}>
            {([
              ['businessDaysOnly',         'Count business days only'],
              ['extensionAllowed',         'Extensions allowed'],
              ['acknowledgmentRequired',   'Acknowledgment required'],
              ['feesAllowed',              'Fees allowed'],
            ] as [keyof Jurisdiction, string][]).map(([key, label]) => (
              <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={Boolean(form[key])}
                  onChange={e => setForm(f => ({ ...f, [key]: e.target.checked }))}
                />
                {label}
              </label>
            ))}
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button className="btn btn-primary" disabled={saving} onClick={handleSave} style={{ gap: 8 }}>
          {saving
            ? <IconLoader2 size={13} style={{ animation: 'spin 1s linear infinite' }} />
            : saved ? <><IconCheck size={13} /> Saved</> : 'Save Changes'
          }
        </button>
      </div>
    </div>
  );
}

// ── Departments Tab ───────────────────────────────────────

function DeptRow({
  dept, onUpdate, onDelete,
}: {
  dept: Department;
  onUpdate: (d: Department) => void;
  onDelete: (id: number) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [form, setForm] = useState({ name: dept.name, code: dept.code, email: dept.email ?? '' });

  async function handleSave() {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const updated = await updateDepartment(dept.recordId, {
        name:  form.name.trim(),
        code:  form.code.trim(),
        email: form.email.trim() || undefined,
      });
      onUpdate(updated);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!confirm(`Delete "${dept.name}"? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await deleteDepartment(dept.recordId);
      onDelete(dept.recordId);
    } finally {
      setDeleting(false);
    }
  }

  async function handleToggleActive() {
    const updated = await updateDepartment(dept.recordId, { isActive: !dept.isActive });
    onUpdate(updated);
  }

  if (editing) {
    return (
      <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--color-neutral-100)', background: 'var(--cds-gray-50)' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 2fr', gap: 8, marginBottom: 8 }}>
          <input
            className="form-input"
            style={{ fontSize: 14 }}
            value={form.name}
            onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            placeholder="Name *"
            autoFocus
          />
          <input
            className="form-input"
            style={{ fontSize: 14 }}
            value={form.code}
            onChange={e => setForm(f => ({ ...f, code: e.target.value }))}
            placeholder="Code"
          />
          <input
            className="form-input"
            style={{ fontSize: 14 }}
            type="email"
            value={form.email}
            onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
            placeholder="Email"
          />
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn btn-secondary btn-sm" onClick={() => { setEditing(false); setForm({ name: dept.name, code: dept.code, email: dept.email ?? '' }); }}>Cancel</button>
          <button className="btn btn-primary btn-sm" disabled={!form.name.trim() || saving} onClick={handleSave}>
            {saving ? <IconLoader2 size={12} style={{ animation: 'spin 1s linear infinite' }} /> : <><IconCheck size={12} /> Save</>}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '10px 14px', borderBottom: '1px solid var(--color-neutral-100)' }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 14, fontWeight: 500, color: dept.isActive ? 'var(--color-neutral-800)' : 'var(--color-neutral-400)', display: 'flex', alignItems: 'center', gap: 8 }}>
          {dept.name}
          {dept.code && <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-neutral-400)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{dept.code}</span>}
          {!dept.isActive && <span style={{ fontSize: 12, color: 'var(--cds-red-700)' }}>Inactive</span>}
        </div>
        {dept.email && <div style={{ fontSize: 12, color: 'var(--color-neutral-400)', marginTop: 2 }}>{dept.email}</div>}
      </div>
      <button className="btn btn-secondary btn-sm" style={{ fontSize: 12 }} onClick={handleToggleActive}>
        {dept.isActive ? 'Deactivate' : 'Activate'}
      </button>
      <button className="btn btn-secondary btn-sm" style={{ fontSize: 12, gap: 3 }} onClick={() => setEditing(true)}>
        Edit
      </button>
      <button
        className="btn btn-secondary btn-sm"
        style={{ padding: '4px 8px', color: 'var(--cds-red-700)' }}
        onClick={handleDelete}
        disabled={deleting}
        title="Delete department"
      >
        {deleting ? <IconLoader2 size={11} style={{ animation: 'spin 1s linear infinite' }} /> : <IconTrash size={11} />}
      </button>
    </div>
  );
}

function DepartmentsTab() {
  const [depts, setDepts]     = useState<Department[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding]   = useState(false);
  const [saving, setSaving]   = useState(false);
  const [form, setForm]       = useState({ name: '', code: '', email: '' });

  useEffect(() => {
    listDepartments({ forceRefresh: true }).then(setDepts).finally(() => setLoading(false));
  }, []);

  async function handleAdd() {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const dept = await createDepartment({
        jurisdictionId: JURISDICTION_ID,
        name:           form.name.trim(),
        code:           form.code.trim(),
        email:          form.email.trim() || undefined,
        isActive:       true,
      });
      setDepts(d => [...d, dept]);
      setForm({ name: '', code: '', email: '' });
      setAdding(false);
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}><IconLoader2 size={20} style={{ animation: 'spin 1s linear infinite', color: 'var(--color-neutral-400)' }} /></div>;

  return (
    <div style={{ maxWidth: 860, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--color-neutral-900)', margin: 0 }}>Departments</h2>
        <button className="btn btn-secondary btn-sm" style={{ gap: 4 }} onClick={() => setAdding(true)}>
          <IconPlus size={12} /> Add Department
        </button>
      </div>

      {adding && (
        <div style={{ background: 'var(--cds-gray-50)', border: '1px solid var(--color-neutral-200)', borderRadius: 4, padding: '14px 16px', marginBottom: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 2fr', gap: 8 }}>
            <div>
              <label style={{ fontSize: 12, color: 'var(--color-neutral-500)', display: 'block', marginBottom: 3 }}>Name *</label>
              <input className="form-input" style={{ fontSize: 14 }} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="City Clerk" autoFocus />
            </div>
            <div>
              <label style={{ fontSize: 12, color: 'var(--color-neutral-500)', display: 'block', marginBottom: 3 }}>Code</label>
              <input className="form-input" style={{ fontSize: 14 }} value={form.code} onChange={e => setForm(f => ({ ...f, code: e.target.value }))} placeholder="CLK" />
            </div>
            <div>
              <label style={{ fontSize: 12, color: 'var(--color-neutral-500)', display: 'block', marginBottom: 3 }}>Email</label>
              <input className="form-input" style={{ fontSize: 14 }} type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="clerk@city.gov" />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className="btn btn-secondary btn-sm" onClick={() => setAdding(false)}>Cancel</button>
            <button className="btn btn-primary btn-sm" disabled={!form.name.trim() || saving} onClick={handleAdd}>
              {saving ? <IconLoader2 size={12} style={{ animation: 'spin 1s linear infinite' }} /> : 'Add'}
            </button>
          </div>
        </div>
      )}

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {depts.length === 0 ? (
          <div style={{ padding: '32px', textAlign: 'center', color: 'var(--color-neutral-400)', fontSize: 14 }}>
            No departments yet. Add a department to enable request routing.
          </div>
        ) : (
          depts.map(dept => (
            <DeptRow
              key={dept.recordId}
              dept={dept}
              onUpdate={updated => setDepts(ds => ds.map(d => d.recordId === updated.recordId ? updated : d))}
              onDelete={id => setDepts(ds => ds.filter(d => d.recordId !== id))}
            />
          ))
        )}
      </div>
    </div>
  );
}

// ── Templates Tab ─────────────────────────────────────────

function TemplatesTab() {
  const [templates, setTemplates] = useState<CorrTemplate[]>([]);
  const [loading, setLoading]     = useState(true);
  const [seeding, setSeeding]     = useState(false);
  const [expanded, setExpanded]   = useState<number | null>(null);

  useEffect(() => {
    listTemplates(JURISDICTION_ID).then(setTemplates).finally(() => setLoading(false));
  }, []);

  async function handleSeedTemplates() {
    setSeeding(true);
    try {
      await seedTemplates(JURISDICTION_ID);
      const updated = await listTemplates(JURISDICTION_ID);
      setTemplates(updated);
    } finally {
      setSeeding(false);
    }
  }

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}><IconLoader2 size={20} style={{ animation: 'spin 1s linear infinite', color: 'var(--color-neutral-400)' }} /></div>;

  return (
    <div style={{ maxWidth: 860, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--color-neutral-900)', margin: 0 }}>Correspondence Templates</h2>
        <button className="btn btn-secondary btn-sm" disabled={seeding} onClick={handleSeedTemplates} style={{ gap: 4 }}>
          {seeding ? <IconLoader2 size={12} style={{ animation: 'spin 1s linear infinite' }} /> : 'Seed / Refresh Defaults'}
        </button>
      </div>

      {templates.length === 0 ? (
        <div style={{ padding: '32px', textAlign: 'center', color: 'var(--color-neutral-400)', fontSize: 14 }}>
          No templates yet. Click "Seed / Refresh Defaults" to populate the standard templates.
        </div>
      ) : (
        <div className="card">
          {templates.map((tmpl, i) => (
            <div key={tmpl.recordId} style={{ borderBottom: i < templates.length - 1 ? '1px solid var(--color-neutral-100)' : 'none' }}>
              <button
                onClick={() => setExpanded(expanded === tmpl.recordId ? null : tmpl.recordId)}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  width: '100%', padding: '12px 14px', border: 'none', background: 'none',
                  cursor: 'pointer', textAlign: 'left',
                }}
              >
                <div>
                  <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--color-neutral-800)' }}>{tmpl.name}</div>
                  <div style={{ fontSize: 12, color: 'var(--color-neutral-400)', marginTop: 2 }}>Trigger: {tmpl.trigger}</div>
                </div>
                {expanded === tmpl.recordId
                  ? <IconChevronUp size={14} style={{ color: 'var(--color-neutral-400)' }} />
                  : <IconChevronDown size={14} style={{ color: 'var(--color-neutral-400)' }} />
                }
              </button>
              {expanded === tmpl.recordId && (
                <div style={{ padding: '0 14px 14px' }}>
                  <div style={{ fontSize: 12, color: 'var(--color-neutral-500)', marginBottom: 6 }}>Subject: {tmpl.subject}</div>
                  <pre style={{
                    fontSize: 12, lineHeight: 1.6, whiteSpace: 'pre-wrap', fontFamily: 'inherit',
                    background: 'var(--cds-gray-50)', border: '1px solid var(--color-neutral-100)',
                    borderRadius: 4, padding: '10px 12px', margin: 0,
                    color: 'var(--color-neutral-700)', maxHeight: 300, overflowY: 'auto',
                  }}>
                    {tmpl.body}
                  </pre>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Exemptions Tab ────────────────────────────────────────

function ExemptionsTab() {
  const [codes, setCodes]     = useState<ExemptionCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding]   = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [form, setForm]       = useState({ code: '', title: '', description: '', citation: '' });

  useEffect(() => {
    listExemptionCodes(JURISDICTION_ID).then(setCodes).finally(() => setLoading(false));
  }, []);

  async function handleSeedExemptions() {
    setSeeding(true);
    try {
      await seedExemptionCodes(JURISDICTION_ID);
      const updated = await listExemptionCodes(JURISDICTION_ID);
      setCodes(updated);
    } finally {
      setSeeding(false);
    }
  }

  async function handleAdd() {
    if (!form.code.trim() || !form.title.trim()) return;
    setSaving(true);
    try {
      const c = await createExemptionCode({
        jurisdictionId:    JURISDICTION_ID,
        code:              form.code.trim(),
        title:             form.title.trim(),
        description:       form.description.trim(),
        statutoryCitation: form.citation.trim(),
        isActive:          true,
      });
      setCodes(cs => [...cs, c]);
      setForm({ code: '', title: '', description: '', citation: '' });
      setAdding(false);
    } finally {
      setSaving(false);
    }
  }

  async function handleToggle(c: ExemptionCode) {
    const updated = await updateExemptionCode(c.recordId, { isActive: !c.isActive });
    setCodes(cs => cs.map(x => x.recordId === c.recordId ? updated : x));
  }

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}><IconLoader2 size={20} style={{ animation: 'spin 1s linear infinite', color: 'var(--color-neutral-400)' }} /></div>;

  const active   = codes.filter(c => c.isActive);
  const inactive = codes.filter(c => !c.isActive);

  return (
    <div style={{ maxWidth: 860, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--color-neutral-900)', margin: 0 }}>Exemption Codes</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary btn-sm" disabled={seeding} onClick={handleSeedExemptions}>
            {seeding ? <IconLoader2 size={12} style={{ animation: 'spin 1s linear infinite' }} /> : 'Seed CC Exemptions'}
          </button>
          <button className="btn btn-secondary btn-sm" style={{ gap: 4 }} onClick={() => setAdding(true)}>
            <IconPlus size={12} /> Add Code
          </button>
        </div>
      </div>

      {adding && (
        <div style={{ background: 'var(--cds-gray-50)', border: '1px solid var(--color-neutral-200)', borderRadius: 4, padding: '14px 16px', marginBottom: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 8 }}>
            <div>
              <label style={{ fontSize: 12, color: 'var(--color-neutral-500)', display: 'block', marginBottom: 3 }}>Code *</label>
              <input className="form-input" style={{ fontSize: 14 }} value={form.code} onChange={e => setForm(f => ({ ...f, code: e.target.value }))} placeholder="7(1)(a)" autoFocus />
            </div>
            <div>
              <label style={{ fontSize: 12, color: 'var(--color-neutral-500)', display: 'block', marginBottom: 3 }}>Title *</label>
              <input className="form-input" style={{ fontSize: 14 }} value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Personnel Records" />
            </div>
          </div>
          <div>
            <label style={{ fontSize: 12, color: 'var(--color-neutral-500)', display: 'block', marginBottom: 3 }}>Statutory Citation</label>
            <input className="form-input" style={{ fontSize: 14 }} value={form.citation} onChange={e => setForm(f => ({ ...f, citation: e.target.value }))} placeholder="Public Records Act § 4(a)" />
          </div>
          <div>
            <label style={{ fontSize: 12, color: 'var(--color-neutral-500)', display: 'block', marginBottom: 3 }}>Description</label>
            <textarea className="form-input" style={{ fontSize: 12, resize: 'vertical' }} rows={2} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className="btn btn-secondary btn-sm" onClick={() => setAdding(false)}>Cancel</button>
            <button className="btn btn-primary btn-sm" disabled={!form.code.trim() || !form.title.trim() || saving} onClick={handleAdd}>
              {saving ? <IconLoader2 size={12} style={{ animation: 'spin 1s linear infinite' }} /> : 'Add Code'}
            </button>
          </div>
        </div>
      )}

      {codes.length === 0 && !adding ? (
        <div style={{ padding: '32px', textAlign: 'center', color: 'var(--color-neutral-400)', fontSize: 14 }}>
          No exemption codes configured. Add codes or seed the standard exemptions.
        </div>
      ) : (
        <div className="card">
          {active.map((c, i) => (
            <div key={c.recordId} style={{
              display: 'flex', alignItems: 'flex-start', gap: 16, padding: '10px 14px',
              borderBottom: i < active.length - 1 || inactive.length > 0 ? '1px solid var(--color-neutral-100)' : 'none',
            }}>
              <code style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-primary-700)', background: 'var(--color-primary-100)', padding: '2px 6px', borderRadius: 4, flexShrink: 0, marginTop: 1 }}>
                {c.code}
              </code>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--color-neutral-800)' }}>{c.title}</div>
                {c.statutoryCitation && <div style={{ fontSize: 12, color: 'var(--color-neutral-400)', marginTop: 2 }}>{c.statutoryCitation}</div>}
                {c.description && <div style={{ fontSize: 12, color: 'var(--color-neutral-500)', marginTop: 4, lineHeight: 1.5 }}>{c.description}</div>}
              </div>
              <button className="btn btn-secondary btn-sm" style={{ fontSize: 12 }} onClick={() => handleToggle(c)}>Disable</button>
            </div>
          ))}
          {inactive.map((c) => (
            <div key={c.recordId} style={{
              display: 'flex', alignItems: 'center', gap: 16, padding: '8px 14px',
              opacity: 0.5, borderTop: '1px solid var(--color-neutral-100)',
            }}>
              <code style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-neutral-500)', background: 'var(--cds-gray-100)', padding: '2px 6px', borderRadius: 4 }}>{c.code}</code>
              <div style={{ flex: 1, fontSize: 12, color: 'var(--color-neutral-500)' }}>{c.title} <span style={{ fontSize: 12, color: 'var(--cds-red-700)' }}>Inactive</span></div>
              <button className="btn btn-secondary btn-sm" style={{ fontSize: 12 }} onClick={() => handleToggle(c)}>Enable</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Workflows Tab ─────────────────────────────────────────

const TASK_TYPE_OPTIONS: { value: TaskType; label: string }[] = [
  { value: 'acknowledge',  label: 'Acknowledgment' },
  { value: 'gather',       label: 'Records Gathering' },
  { value: 'review',       label: 'Review' },
  { value: 'redact',       label: 'Redaction' },
  { value: 'legalReview',  label: 'Legal Review' },
  { value: 'approve',      label: 'Approval' },
  { value: 'invoice',      label: 'Invoice / Fees' },
  { value: 'release',      label: 'Release' },
  { value: 'other',        label: 'Other' },
];

function WorkflowStepEditor({
  step, index, total,
  onUpdate, onDelete, onMoveUp, onMoveDown,
}: {
  step: WorkflowStep;
  index: number;
  total: number;
  onUpdate: (id: number, patch: Partial<WorkflowStep>) => void;
  onDelete: (id: number) => void;
  onMoveUp: (index: number) => void;
  onMoveDown: (index: number) => void;
}) {
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: '24px 1fr 140px 70px 80px 80px 28px',
      gap: 8, alignItems: 'center',
      padding: '8px 10px', background: 'white',
      border: '1px solid var(--color-neutral-200)', borderRadius: 7, marginBottom: 6,
    }}>
      {/* Step number */}
      <span style={{ fontSize: 12, color: 'var(--color-neutral-400)', textAlign: 'center', fontWeight: 600 }}>
        {index + 1}
      </span>

      {/* Name */}
      <input
        className="form-input"
        style={{ fontSize: 12 }}
        value={step.name}
        onChange={e => onUpdate(step.recordId, { name: e.target.value })}
        placeholder="Step name"
      />

      {/* Task type */}
      <select
        className="form-input"
        style={{ fontSize: 12 }}
        value={step.taskType}
        onChange={e => onUpdate(step.recordId, { taskType: e.target.value as TaskType })}
      >
        {TASK_TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>

      {/* Days to complete */}
      <div>
        <input
          type="number"
          className="form-input"
          style={{ fontSize: 12, textAlign: 'center' }}
          min={1} max={90}
          value={step.daysToComplete}
          onChange={e => onUpdate(step.recordId, { daysToComplete: Number(e.target.value) })}
          title="Days to complete"
        />
      </div>

      {/* Flags */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, cursor: 'pointer' }}>
          <input type="checkbox" checked={step.isClockPausing} onChange={e => onUpdate(step.recordId, { isClockPausing: e.target.checked })} />
          Pauses clock
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, cursor: 'pointer' }}>
          <input type="checkbox" checked={step.requiresApproval} onChange={e => onUpdate(step.recordId, { requiresApproval: e.target.checked })} />
          Needs approval
        </label>
      </div>

      {/* Reorder */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <button
          style={{ border: 'none', background: 'none', cursor: index === 0 ? 'not-allowed' : 'pointer', padding: 2, opacity: index === 0 ? 0.3 : 1 }}
          onClick={() => onMoveUp(index)} disabled={index === 0}
        ><IconArrowUp size={11} /></button>
        <button
          style={{ border: 'none', background: 'none', cursor: index === total - 1 ? 'not-allowed' : 'pointer', padding: 2, opacity: index === total - 1 ? 0.3 : 1 }}
          onClick={() => onMoveDown(index)} disabled={index === total - 1}
        ><IconArrowDown size={11} /></button>
      </div>

      {/* Delete */}
      <button
        style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--cds-red-700)', padding: 2 }}
        onClick={() => onDelete(step.recordId)}
      ><IconTrash size={13} /></button>
    </div>
  );
}

function WorkflowEditor({ workflow, onClose }: { workflow: Workflow; onClose: () => void }) {
  const [steps, setSteps]   = useState<WorkflowStep[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [name, setName]       = useState(workflow.name);
  const [desc, setDesc]       = useState(workflow.description ?? '');
  const [isActive, setIsActive] = useState(workflow.isActive);

  useEffect(() => {
    listWorkflowSteps(workflow.recordId).then(s => { setSteps(s); setLoading(false); });
  }, [workflow.recordId]);

  async function handleSaveWorkflow() {
    setSaving(true);
    try {
      await updateWorkflow(workflow.recordId, { name, description: desc, isActive });
    } finally {
      setSaving(false);
    }
  }

  async function handleAddStep() {
    const newStep = await createWorkflowStep({
      workflowId:     workflow.recordId,
      stepOrder:      steps.length + 1,
      name:           'New Step',
      taskType:       'other',
      daysToComplete: 3,
    });
    setSteps(s => [...s, newStep]);
  }

  async function handleUpdateStep(id: number, patch: Partial<WorkflowStep>) {
    // Optimistic update
    setSteps(prev => prev.map(s => s.recordId === id ? { ...s, ...patch } : s));
    await updateWorkflowStep(id, patch);
  }

  async function handleDeleteStep(id: number) {
    setSteps(prev => prev.filter(s => s.recordId !== id));
    await deleteWorkflowStep(id);
    // Re-number remaining steps
    setSteps(prev => {
      prev.forEach((s, i) => {
        if (s.stepOrder !== i + 1) updateWorkflowStep(s.recordId, { stepOrder: i + 1 });
      });
      return prev.map((s, i) => ({ ...s, stepOrder: i + 1 }));
    });
  }

  async function handleMove(index: number, dir: 'up' | 'down') {
    const newSteps = [...steps];
    const target = dir === 'up' ? index - 1 : index + 1;
    [newSteps[index], newSteps[target]] = [newSteps[target], newSteps[index]];
    const renumbered = newSteps.map((s, i) => ({ ...s, stepOrder: i + 1 }));
    setSteps(renumbered);
    await Promise.all(renumbered.map(s => updateWorkflowStep(s.recordId, { stepOrder: s.stepOrder })));
  }

  return (
    <div style={{ background: 'var(--cds-gray-50)', border: '1px solid var(--color-neutral-200)', borderRadius: 4, padding: 24, marginBottom: 16 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              className="form-input"
              style={{ fontSize: 14, fontWeight: 600, flex: 1 }}
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Workflow name"
            />
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, cursor: 'pointer' }}>
              <input type="checkbox" checked={isActive} onChange={e => setIsActive(e.target.checked)} />
              Active
            </label>
            <button className="btn btn-secondary btn-sm" disabled={saving} onClick={handleSaveWorkflow}>
              {saving ? <IconLoader2 size={12} style={{ animation: 'spin 1s linear infinite' }} /> : <><IconCheck size={12} /> Save</>}
            </button>
            <button className="btn btn-secondary btn-sm" onClick={onClose}>Done</button>
          </div>
          <input
            className="form-input"
            style={{ fontSize: 12 }}
            value={desc}
            onChange={e => setDesc(e.target.value)}
            placeholder="Description (optional)"
          />
        </div>
      </div>

      {/* Column headers */}
      <div style={{ display: 'grid', gridTemplateColumns: '24px 1fr 140px 70px 80px 80px 28px', gap: 8, padding: '0 10px', marginBottom: 4 }}>
        {['#', 'Step Name', 'Task Type', 'Days', 'Flags', '', ''].map((h, i) => (
          <span key={i} style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-neutral-400)' }}>{h}</span>
        ))}
      </div>

      {/* Steps */}
      {loading ? (
        <div style={{ padding: 24, textAlign: 'center' }}><IconLoader2 size={18} style={{ animation: 'spin 1s linear infinite' }} /></div>
      ) : steps.length === 0 ? (
        <div style={{ padding: 16, textAlign: 'center', fontSize: 12, color: 'var(--color-neutral-400)' }}>No steps yet. Add a step to get started.</div>
      ) : (
        steps.map((step, i) => (
          <WorkflowStepEditor
            key={step.recordId}
            step={step} index={i} total={steps.length}
            onUpdate={handleUpdateStep}
            onDelete={handleDeleteStep}
            onMoveUp={idx => handleMove(idx, 'up')}
            onMoveDown={idx => handleMove(idx, 'down')}
          />
        ))
      )}

      <button className="btn btn-secondary btn-sm" style={{ marginTop: 8, gap: 4 }} onClick={handleAddStep}>
        <IconPlus size={12} /> Add Step
      </button>
    </div>
  );
}

// ── Seed data ────────────────────────────────────────────

const SEED_WORKFLOWS: Array<{
  name: string;
  description: string;
  steps: Array<Omit<CreateWorkflowStepInput, 'workflowId'>>;
}> = [
  {
    name: 'Standard FOIA Response',
    description: 'Default workflow for routine public records requests',
    steps: [
      { stepOrder: 1, name: 'Acknowledge Receipt',        taskType: 'acknowledge', daysToComplete: 2,  isClockPausing: false, requiresApproval: false },
      { stepOrder: 2, name: 'Gather Responsive Records',  taskType: 'gather',      daysToComplete: 10, isClockPausing: false, requiresApproval: false },
      { stepOrder: 3, name: 'Review for Exemptions',      taskType: 'review',      daysToComplete: 3,  isClockPausing: false, requiresApproval: false },
      { stepOrder: 4, name: 'Redact Exempt Material',     taskType: 'redact',      daysToComplete: 3,  isClockPausing: false, requiresApproval: false },
      { stepOrder: 5, name: 'Supervisor Approval',        taskType: 'approve',     daysToComplete: 2,  isClockPausing: false, requiresApproval: true  },
      { stepOrder: 6, name: 'Release to Requester',       taskType: 'release',     daysToComplete: 1,  isClockPausing: false, requiresApproval: false },
    ],
  },
  {
    name: 'Complex / Voluminous Request',
    description: 'For large or sensitive requests requiring legal review',
    steps: [
      { stepOrder: 1, name: 'Acknowledge Receipt',        taskType: 'acknowledge', daysToComplete: 2,  isClockPausing: false, requiresApproval: false },
      { stepOrder: 2, name: 'Fee Estimate',               taskType: 'invoice',     daysToComplete: 3,  isClockPausing: true,  requiresApproval: false },
      { stepOrder: 3, name: 'Gather Responsive Records',  taskType: 'gather',      daysToComplete: 15, isClockPausing: false, requiresApproval: false },
      { stepOrder: 4, name: 'Legal Review',               taskType: 'legalReview', daysToComplete: 5,  isClockPausing: false, requiresApproval: true  },
      { stepOrder: 5, name: 'Redact Exempt Material',     taskType: 'redact',      daysToComplete: 5,  isClockPausing: false, requiresApproval: false },
      { stepOrder: 6, name: 'Supervisor Approval',        taskType: 'approve',     daysToComplete: 2,  isClockPausing: false, requiresApproval: true  },
      { stepOrder: 7, name: 'Release to Requester',       taskType: 'release',     daysToComplete: 1,  isClockPausing: false, requiresApproval: false },
    ],
  },
  {
    name: 'Denial Workflow',
    description: 'For requests that will be denied in whole or part',
    steps: [
      { stepOrder: 1, name: 'Acknowledge Receipt',        taskType: 'acknowledge', daysToComplete: 2,  isClockPausing: false, requiresApproval: false },
      { stepOrder: 2, name: 'Review Request & Records',   taskType: 'review',      daysToComplete: 7,  isClockPausing: false, requiresApproval: false },
      { stepOrder: 3, name: 'Legal Review of Exemptions', taskType: 'legalReview', daysToComplete: 5,  isClockPausing: false, requiresApproval: true  },
      { stepOrder: 4, name: 'Draft Denial Letter',        taskType: 'other',       daysToComplete: 2,  isClockPausing: false, requiresApproval: false },
      { stepOrder: 5, name: 'Supervisor Approval',        taskType: 'approve',     daysToComplete: 2,  isClockPausing: false, requiresApproval: true  },
      { stepOrder: 6, name: 'Send Denial & Appeal Notice',taskType: 'release',     daysToComplete: 1,  isClockPausing: false, requiresApproval: false },
    ],
  },
  {
    name: 'Quick Release',
    description: 'Simple requests with readily available records',
    steps: [
      { stepOrder: 1, name: 'Acknowledge Receipt',        taskType: 'acknowledge', daysToComplete: 1,  isClockPausing: false, requiresApproval: false },
      { stepOrder: 2, name: 'Locate & Review Records',    taskType: 'gather',      daysToComplete: 3,  isClockPausing: false, requiresApproval: false },
      { stepOrder: 3, name: 'Release to Requester',       taskType: 'release',     daysToComplete: 1,  isClockPausing: false, requiresApproval: false },
    ],
  },
];

function WorkflowsTab() {
  const [workflows, setWorkflows]   = useState<Workflow[]>([]);
  const [loading, setLoading]       = useState(true);
  const [adding, setAdding]         = useState(false);
  const [saving, setSaving]         = useState(false);
  const [seeding, setSeeding]       = useState(false);
  const [editingId, setEditingId]   = useState<number | null>(null);
  const [newName, setNewName]       = useState('');
  const [newDesc, setNewDesc]       = useState('');

  useEffect(() => {
    listWorkflows(JURISDICTION_ID).then(setWorkflows).finally(() => setLoading(false));
  }, []);

  async function handleCreate() {
    if (!newName.trim()) return;
    setSaving(true);
    try {
      const w = await createWorkflow({ jurisdictionId: JURISDICTION_ID, name: newName.trim(), description: newDesc });
      setWorkflows(ws => [...ws, w]);
      setNewName(''); setNewDesc(''); setAdding(false);
      setEditingId(w.recordId);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(w: Workflow) {
    if (!confirm(`Delete workflow "${w.name}" and all its steps? This cannot be undone.`)) return;
    await deleteWorkflow(w.recordId);
    setWorkflows(ws => ws.filter(x => x.recordId !== w.recordId));
    if (editingId === w.recordId) setEditingId(null);
  }

  async function handleSeed() {
    if (!confirm('Create 4 example workflows? Existing workflows will not be affected.')) return;
    setSeeding(true);
    try {
      for (const template of SEED_WORKFLOWS) {
        const w = await createWorkflow({
          jurisdictionId: JURISDICTION_ID,
          name:           template.name,
          description:    template.description,
          isActive:       true,
        });
        await Promise.all(template.steps.map(s => createWorkflowStep({ ...s, workflowId: w.recordId })));
        setWorkflows(ws => [...ws, w]);
      }
    } finally {
      setSeeding(false);
    }
  }

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}><IconLoader2 size={22} style={{ animation: 'spin 1s linear infinite' }} /></div>;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--color-neutral-900)', margin: '0 0 4px' }}>Workflows</h2>
          <p style={{ fontSize: 12, color: 'var(--color-neutral-500)', margin: 0 }}>
            Define reusable task sequences. Apply a workflow to a request to auto-create all tasks.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {workflows.length === 0 && (
            <button className="btn btn-secondary btn-sm" style={{ gap: 4 }} onClick={handleSeed} disabled={seeding}>
              {seeding ? <IconLoader2 size={12} style={{ animation: 'spin 1s linear infinite' }} /> : <><IconGitBranch size={12} /> Seed Examples</>}
            </button>
          )}
          <button className="btn btn-primary btn-sm" style={{ gap: 4 }} onClick={() => setAdding(true)}>
            <IconPlus size={13} /> New Workflow
          </button>
        </div>
      </div>

      {adding && (
        <div style={{ background: 'var(--cds-gray-50)', border: '1px solid var(--color-neutral-200)', borderRadius: 4, padding: 16, marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', gap: 8 }}>
            <input className="form-input" style={{ flex: 1, fontSize: 14 }} placeholder="Workflow name *" value={newName} onChange={e => setNewName(e.target.value)} autoFocus />
            <input className="form-input" style={{ flex: 2, fontSize: 12 }} placeholder="Description (optional)" value={newDesc} onChange={e => setNewDesc(e.target.value)} />
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className="btn btn-secondary btn-sm" onClick={() => { setAdding(false); setNewName(''); setNewDesc(''); }}>Cancel</button>
            <button className="btn btn-primary btn-sm" disabled={!newName.trim() || saving} onClick={handleCreate}>
              {saving ? <IconLoader2 size={12} style={{ animation: 'spin 1s linear infinite' }} /> : 'Create & Edit Steps'}
            </button>
          </div>
        </div>
      )}

      {workflows.length === 0 && !adding && (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--color-neutral-400)', fontSize: 14 }}>
          No workflows yet. Create one or use <strong>Seed Examples</strong> to get started quickly.
        </div>
      )}

      {workflows.map(w => (
        <div key={w.recordId}>
          {editingId === w.recordId ? (
            <WorkflowEditor workflow={w} onClose={() => setEditingId(null)} />
          ) : (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 16,
              padding: '12px 16px', background: 'white',
              border: '1px solid var(--color-neutral-200)', borderRadius: 4, marginBottom: 8,
            }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--color-neutral-800)' }}>{w.name}</span>
                  <span style={{
                    fontSize: 12, padding: '1px 7px', borderRadius: 99, fontWeight: 600,
                    background: w.isActive ? 'var(--cds-green-50)' : 'var(--cds-gray-100)',
                    color: w.isActive ? 'var(--cds-green-700)' : 'var(--cds-gray-600)',
                  }}>{w.isActive ? 'Active' : 'Inactive'}</span>
                </div>
                {w.description && <div style={{ fontSize: 12, color: 'var(--color-neutral-500)', marginTop: 2 }}>{w.description}</div>}
              </div>
              <button className="btn btn-secondary btn-sm" style={{ gap: 4 }} onClick={() => setEditingId(w.recordId)}>
                Edit Steps
              </button>
              <button
                className="btn btn-secondary btn-sm"
                style={{ padding: '4px 8px', color: 'var(--cds-red-700)' }}
                onClick={() => handleDelete(w)}
                title="Delete workflow"
              >
                <IconTrash size={12} />
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ── Holidays Tab ─────────────────────────────────────────

function HolidaysTab() {
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [loading, setLoading]   = useState(true);
  const [adding, setAdding]     = useState(false);
  const [form, setForm]         = useState({ date: '', name: '', isRecurring: false });
  const [saving, setSaving]     = useState(false);

  useEffect(() => {
    listHolidays(JURISDICTION_ID).then(setHolidays).finally(() => setLoading(false));
  }, []);

  async function handleAdd() {
    if (!form.date || !form.name) return;
    setSaving(true);
    try {
      const h = await createHoliday({ jurisdictionId: JURISDICTION_ID, ...form });
      setHolidays(hs => [...hs, h].sort((a, b) => a.date.localeCompare(b.date)));
      setForm({ date: '', name: '', isRecurring: false });
      setAdding(false);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(recordId: number) {
    if (!confirm('Remove this holiday?')) return;
    await deleteHoliday(recordId);
    setHolidays(hs => hs.filter(h => h.recordId !== recordId));
  }

  return (
    <div style={{ maxWidth: 640, margin: '0 auto' }}>
      <div className="card">
        <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--color-neutral-100)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--color-neutral-800)' }}>Holiday Calendar</div>
            <div style={{ fontSize: 12, color: 'var(--color-neutral-500)', marginTop: 2 }}>
              Holidays are skipped when calculating business-day deadlines.
            </div>
          </div>
          <button className="btn btn-primary btn-sm" style={{ gap: 4 }} onClick={() => setAdding(true)}>
            <IconPlus size={13} /> Add Holiday
          </button>
        </div>

        {adding && (
          <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--color-neutral-100)', background: 'var(--color-neutral-50)', display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div className="form-field" style={{ marginBottom: 0 }}>
              <label className="form-label">Date</label>
              <input type="date" className="form-input" style={{ fontSize: 14 }}
                value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} />
            </div>
            <div className="form-field" style={{ flex: 1, minWidth: 160, marginBottom: 0 }}>
              <label className="form-label">Name</label>
              <input type="text" className="form-input" style={{ fontSize: 14 }} placeholder="e.g. Independence Day"
                value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--color-neutral-600)', cursor: 'pointer', paddingBottom: 6 }}>
              <input type="checkbox" checked={form.isRecurring} onChange={e => setForm(f => ({ ...f, isRecurring: e.target.checked }))} />
              Recurring annually
            </label>
            <div style={{ display: 'flex', gap: 8, paddingBottom: 2 }}>
              <button className="btn btn-secondary btn-sm" onClick={() => setAdding(false)}>Cancel</button>
              <button className="btn btn-primary btn-sm" onClick={handleAdd} disabled={saving || !form.date || !form.name}>
                {saving ? <IconLoader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> : 'Save'}
              </button>
            </div>
          </div>
        )}

        {loading ? (
          <div style={{ padding: 32, textAlign: 'center', color: 'var(--color-neutral-400)' }}>
            <IconLoader2 size={20} style={{ animation: 'spin 1s linear infinite' }} />
          </div>
        ) : holidays.length === 0 ? (
          <div style={{ padding: '24px 16px', textAlign: 'center', fontSize: 14, color: 'var(--color-neutral-400)' }}>
            No holidays configured. Add federal and local holidays to keep deadlines accurate.
          </div>
        ) : (
          <table className="data-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th style={{ width: 130, textAlign: 'center' }}>Date</th>
                <th style={{ textAlign: 'center' }}>Holiday</th>
                <th style={{ width: 100, textAlign: 'center' }}>Type</th>
                <th style={{ width: 48, textAlign: 'center' }} />
              </tr>
            </thead>
            <tbody>
              {holidays.map(h => {
                const d = new Date(h.date + (h.date.includes('T') ? '' : 'T00:00:00'));
                const formatted = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                const weekday = d.toLocaleDateString('en-US', { weekday: 'short' });
                return (
                  <tr key={h.recordId}>
                    <td style={{ textAlign: 'center' }}>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{formatted}</div>
                      <div style={{ fontSize: 12, color: 'var(--color-neutral-400)' }}>{weekday}</div>
                    </td>
                    <td style={{ fontWeight: 500, textAlign: 'center' }}>{h.name}</td>
                    <td style={{ textAlign: 'center' }}>
                      <span style={{
                        display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 12, fontWeight: 600,
                        background: h.isRecurring ? 'var(--color-primary-100)' : 'var(--color-neutral-100)',
                        color: h.isRecurring ? 'var(--color-primary-700)' : 'var(--color-neutral-500)',
                      }}>
                        {h.isRecurring ? '↻ Annual' : 'One-time'}
                      </span>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <button
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-neutral-400)', padding: 4 }}
                        onClick={() => handleDelete(h.recordId)}
                        title="Remove"
                      >
                        <IconTrash size={14} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ── Maintenance Tab ───────────────────────────────────────

function MaintenanceTab() {
  const [running, setRunning]   = useState(false);
  const [result, setResult]     = useState<string | null>(null);

  async function handleBackfillPriority() {
    setRunning(true);
    setResult(null);
    try {
      const count = await backfillPriority();
      setResult(count === 0
        ? 'All records already have a priority set — nothing to update.'
        : `Done. ${count} record${count !== 1 ? 's' : ''} updated to priority "normal".`);
    } catch (err) {
      setResult(`Error: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setRunning(false);
    }
  }

  return (
    <div style={{ maxWidth: 640, margin: '0 auto' }}>
      <div className="card">
        <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--color-neutral-100)' }}>
          <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--color-neutral-800)' }}>Data Migrations</div>
          <div style={{ fontSize: 12, color: 'var(--color-neutral-500)', marginTop: 2 }}>One-time fixes for existing records. Safe to run multiple times.</div>
        </div>
        <div style={{ padding: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--color-neutral-800)', marginBottom: 2 }}>Backfill missing priority → normal</div>
              <div style={{ fontSize: 12, color: 'var(--color-neutral-500)' }}>
                Sets priority to "normal" on any request where the field is empty. New requests already default to "normal".
              </div>
            </div>
            <button
              className="btn btn-secondary btn-sm"
              style={{ flexShrink: 0 }}
              onClick={handleBackfillPriority}
              disabled={running}
            >
              {running
                ? <><IconLoader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> Running…</>
                : 'Run'}
            </button>
          </div>
          {result && (
            <div style={{
              marginTop: 12, padding: '8px 12px', borderRadius: 4, fontSize: 12,
              background: result.startsWith('Error') ? '#FBEFED' : 'var(--cds-green-50)',
              color: result.startsWith('Error') ? 'var(--cds-red-700)' : 'var(--cds-green-700)',
              border: `1px solid ${result.startsWith('Error') ? '#F7D5D0' : 'var(--cds-green-200)'}`,
            }}>
              {result}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Library Management Tab ───────────────────────────────

const LIBRARY_CATEGORIES = [
  'Council & Board Meetings', 'Budgets & Finance', 'Policies & Ordinances',
  'Reports & Studies', 'Contracts & Procurement', 'Public Safety',
  'Planning & Zoning', 'Other',
];

function LibraryTab() {
  const [docs, setDocs] = useState<LibraryDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [form, setForm] = useState({ title: '', description: '', category: 'Other' });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const actorEmail = useAuthStore(s => s.user?.email) ?? '';

  useEffect(() => {
    listLibraryDocuments().then(setDocs).finally(() => setLoading(false));
  }, []);

  async function handleUpload() {
    if (!pendingFile || !form.title.trim()) return;
    setUploading(true);
    try {
      const doc = await createLibraryDocument({
        title: form.title.trim(),
        description: form.description.trim(),
        category: form.category,
        file: pendingFile,
        uploadedBy: actorEmail,
      });
      setDocs(prev => [doc, ...prev]);
      setForm({ title: '', description: '', category: 'Other' });
      setPendingFile(null);
      setAdding(false);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  }

  async function handleTogglePublish(doc: LibraryDocument) {
    const next = !doc.isPublished;
    await updateLibraryDocument(doc.recordId, { isPublished: next });
    setDocs(prev => prev.map(d => d.recordId === doc.recordId ? { ...d, isPublished: next } : d));
  }

  async function handleDelete(doc: LibraryDocument) {
    if (!confirm(`Delete "${doc.title}"? This cannot be undone.`)) return;
    await deleteLibraryDocument(doc.recordId);
    setDocs(prev => prev.filter(d => d.recordId !== doc.recordId));
  }

  function formatBytes(bytes: number) {
    if (!bytes) return '—';
    const k = 1024, sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  }

  const published = docs.filter(d => d.isPublished).length;

  return (
    <div style={{ maxWidth: 860, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--color-neutral-900)', margin: 0 }}>Public Records Library</h2>
          <div style={{ fontSize: 12, color: 'var(--color-neutral-500)', marginTop: 4 }}>
            Upload general government documents — council minutes, budgets, reports — for the public to browse and download. These are separate from FOIA request responses.
          </div>
        </div>
        <button className="btn btn-primary btn-sm" style={{ gap: 4 }} onClick={() => setAdding(true)}>
          <IconPlus size={14} /> Add Document
        </button>
      </div>

      {/* Stats */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <span style={{ fontSize: 12, fontWeight: 600, padding: '4px 8px', borderRadius: 4, background: 'var(--color-primary-50)', color: 'var(--color-primary-700)' }}>
          {docs.length} total
        </span>
        <span style={{ fontSize: 12, fontWeight: 600, padding: '4px 8px', borderRadius: 4, background: 'var(--cds-green-50)', color: 'var(--cds-green-700)' }}>
          {published} published
        </span>
        {docs.length > published && (
          <span style={{ fontSize: 12, fontWeight: 600, padding: '4px 8px', borderRadius: 4, background: 'var(--color-neutral-100)', color: 'var(--color-neutral-500)' }}>
            {docs.length - published} unpublished
          </span>
        )}
      </div>

      {/* Add form */}
      {adding && (
        <div className="card" style={{ marginBottom: 16, padding: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 16 }}>Upload New Document</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <input type="file" ref={fileInputRef} style={{ display: 'none' }}
              accept=".pdf,.doc,.docx,.xlsx,.xls,.csv,.pptx,.png,.jpg,.jpeg,.zip"
              onChange={e => { setPendingFile(e.target.files?.[0] ?? null); e.target.value = ''; }} />

            <div className="form-field" style={{ marginBottom: 0 }}>
              <label className="form-label required">Title</label>
              <input type="text" className="form-input" value={form.title}
                onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                placeholder="e.g. City Council Meeting Minutes — May 2026" />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div className="form-field" style={{ marginBottom: 0 }}>
                <label className="form-label">Category</label>
                <select className="form-select" value={form.category}
                  onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
                  {LIBRARY_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="form-field" style={{ marginBottom: 0 }}>
                <label className="form-label">File</label>
                <button className="btn btn-secondary" style={{ width: '100%', justifyContent: 'center' }}
                  onClick={() => fileInputRef.current?.click()}>
                  {pendingFile ? pendingFile.name : 'Choose file…'}
                </button>
              </div>
            </div>

            <div className="form-field" style={{ marginBottom: 0 }}>
              <label className="form-label">Description (optional)</label>
              <textarea className="form-textarea" rows={2} value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Brief description of the document contents" />
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary btn-sm" onClick={() => { setAdding(false); setPendingFile(null); }}>Cancel</button>
              <button className="btn btn-primary btn-sm" disabled={uploading || !pendingFile || !form.title.trim()}
                onClick={handleUpload}>
                {uploading ? <><IconLoader2 size={12} style={{ animation: 'spin 1s linear infinite' }} /> Uploading…</> : 'Upload & Publish'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Document list */}
      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--color-neutral-400)' }}>
          <IconLoader2 size={20} style={{ animation: 'spin 1s linear infinite' }} />
        </div>
      ) : docs.length === 0 && !adding ? (
        <div className="card" style={{ textAlign: 'center', padding: 32, color: 'var(--color-neutral-400)' }}>
          <IconFileText size={32} style={{ opacity: 0.4, marginBottom: 8 }} />
          <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--color-neutral-600)' }}>No library documents yet</div>
          <div style={{ fontSize: 12, marginTop: 4 }}>Upload council minutes, budgets, reports, and other public documents.</div>
        </div>
      ) : (
        <div className="card" style={{ padding: 0 }}>
          <table className="data-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>Title</th>
                <th style={{ width: 140 }}>Category</th>
                <th style={{ width: 80 }}>Size</th>
                <th style={{ width: 100 }}>Uploaded</th>
                <th style={{ width: 90 }}>Status</th>
                <th style={{ width: 80 }} />
              </tr>
            </thead>
            <tbody>
              {docs.map(doc => (
                <tr key={doc.recordId}>
                  <td>
                    <div style={{ fontWeight: 500 }}>{doc.title || doc.fileName}</div>
                    {doc.description && (
                      <div style={{ fontSize: 12, color: 'var(--color-neutral-400)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 300 }}>
                        {doc.description}
                      </div>
                    )}
                  </td>
                  <td><span style={{ fontSize: 12, padding: '2px 8px', borderRadius: 4, background: 'var(--color-neutral-100)', color: 'var(--color-neutral-600)' }}>{doc.category}</span></td>
                  <td style={{ fontSize: 12, color: 'var(--color-neutral-400)' }}>{formatBytes(doc.fileSize)}</td>
                  <td style={{ fontSize: 12, color: 'var(--color-neutral-500)' }}>{doc.uploadedAt ? new Date(doc.uploadedAt).toLocaleDateString() : '—'}</td>
                  <td>
                    <button
                      className="btn btn-secondary btn-sm"
                      style={{
                        fontSize: 12, padding: '2px 8px',
                        color: doc.isPublished ? 'var(--cds-green-700)' : 'var(--color-neutral-500)',
                        background: doc.isPublished ? 'var(--cds-green-50)' : 'var(--color-neutral-100)',
                        border: `1px solid ${doc.isPublished ? 'var(--cds-green-200)' : 'var(--color-neutral-200)'}`,
                      }}
                      onClick={() => handleTogglePublish(doc)}
                    >
                      {doc.isPublished ? '● Live' : '○ Draft'}
                    </button>
                  </td>
                  <td>
                    <button className="btn btn-secondary btn-sm" style={{ fontSize: 12, color: 'var(--cds-red-700)', padding: '2px 8px' }}
                      onClick={() => handleDelete(doc)}>
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Main Settings Page ────────────────────────────────────

export default function Settings() {
  const [activeTab, setActiveTab] = useState('jurisdiction');

  return (
    <div style={{ maxWidth: 960, margin: '0 auto' }}>
      <h1 style={{ fontSize: 24, fontWeight: 700, color: 'var(--color-neutral-900)', margin: '0 0 24px' }}>
        System Settings
      </h1>

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 2, borderBottom: '2px solid var(--color-neutral-200)', marginBottom: 24 }}>
        {SETTINGS_TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            style={{
              display: 'flex', alignItems: 'center', gap: 5, padding: '8px 16px',
              fontSize: 14, fontWeight: activeTab === id ? 600 : 400,
              color: activeTab === id ? 'var(--color-primary-700)' : 'var(--color-neutral-600)',
              border: 'none', background: 'none', cursor: 'pointer',
              borderBottom: activeTab === id ? '2px solid var(--color-primary-700)' : '2px solid transparent',
              marginBottom: -2, transition: 'color 0.15s',
            }}
          >
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>

      {activeTab === 'jurisdiction' && <JurisdictionTab />}
      {activeTab === 'departments'  && <DepartmentsTab />}
      {activeTab === 'templates'    && <TemplatesTab />}
      {activeTab === 'exemptions'   && <ExemptionsTab />}
      {activeTab === 'workflows'    && <WorkflowsTab />}
      {activeTab === 'holidays'     && <HolidaysTab />}
      {activeTab === 'library'      && <LibraryTab />}
      {activeTab === 'maintenance'  && <MaintenanceTab />}
    </div>
  );
}
